import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { API_URLS } from '../../constants/api';
import ChargeReportsTable from './components/ChargeReportsTable';
import ReportsSummary from './components/ReportsSummary';
import ReportsFilters from './components/ReportsFilters';

const Reports = () => {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState('');
  const [filters, setFilters] = useState({
    courthouse: '',
    judge: '',
    prosecutor: '',
    chargeCode: '',
    minFrequency: 1
  });
  const [activeTab, setActiveTab] = useState('summary');

  // Fetch available years
  const { data: availableYears, isLoading: yearsLoading } = useFetch(
    API_URLS.REPORTS_YEARS
  );

  // Set default year when years are loaded
  useEffect(() => {
    if (availableYears && availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[availableYears.length - 1]); // Default to most recent year
    }
  }, [availableYears, selectedYear]);

  // Fetch report summary for selected year
  const { data: reportSummary, isLoading: summaryLoading } = useFetch(
    selectedYear ? `${API_URLS.REPORTS_SUMMARY}/${selectedYear}` : null
  );

  // Fetch charge reports for selected year and filters
  const { data: chargeReports, isLoading: reportsLoading } = useFetch(
    selectedYear
      ? `${API_URLS.REPORTS_CHARGES}/${selectedYear}?${new URLSearchParams({
          ...(filters.courthouse && { courthouse: filters.courthouse }),
          ...(filters.judge && { judge: filters.judge }),
          ...(filters.prosecutor && { prosecutor: filters.prosecutor }),
          ...(filters.chargeCode && { chargeCode: filters.chargeCode }),
          minFrequency: filters.minFrequency.toString()
        })}`
      : null
  );

  const handleYearChange = (year) => {
    setSelectedYear(year);
    // Reset filters when year changes
    setFilters({
      courthouse: '',
      judge: '',
      prosecutor: '',
      chargeCode: '',
      minFrequency: 1
    });
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters({
      courthouse: '',
      judge: '',
      prosecutor: '',
      chargeCode: '',
      minFrequency: 1
    });
  };

  if (yearsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!availableYears || availableYears.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            No Reports Available
          </h2>
          <p className="text-gray-600">
            No case data found to generate reports.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/app')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Back to Search</span>
                  <span className="sm:hidden">Back</span>
                </button>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Overall Charge Reports
                  </h1>
                  <p className="mt-2 text-sm text-gray-600">
                    Analyze charge statistics, conviction rates, and case
                    outcomes
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-w-0 sm:min-w-[120px]"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('summary')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'summary'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => setActiveTab('charges')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'charges'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Charge Details
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'summary' && (
          <div className="space-y-6">
            <ReportsSummary
              summary={reportSummary}
              isLoading={summaryLoading}
              year={selectedYear}
            />
          </div>
        )}

        {activeTab === 'charges' && (
          <div className="space-y-6">
            <ReportsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              reportSummary={reportSummary}
              year={selectedYear}
            />
            <ChargeReportsTable
              reports={chargeReports}
              isLoading={reportsLoading}
              year={selectedYear}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
